import { useState, useEffect, useMemo } from "react";
import {
  MessageSquare, Gift, Sparkles, Star, Shield, Crown,
  ArrowRight, Lightbulb, BookOpen, GraduationCap, Flower2, Sun, Leaf, Snowflake,
  Cake, Heart, Bell, Megaphone, PartyPopper, Tag, Tv,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import tips, { getDailyTip } from "@/data/tipsData";
import ALL_TREATMENTS from "@/data/treatmentsCatalog";
import { openContactModal } from "@/components/ContactModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Whitelist of icon names accepted in custom admin messages → React components.
// New icon options can be added here without changing the API.
const CUSTOM_ICON_MAP = {
  Sparkles, Cake, Heart, Bell, Megaphone, PartyPopper, Tag, Gift, Crown,
  Star, Lightbulb, BookOpen, GraduationCap, Flower2, Sun, Leaf, Snowflake,
};

// Curated short education snippets (kept from the old colored promo strip)
const EDUCATION_SNIPPETS = [
  { tag: "BOTOX", en: "Preventive Botox in your late 20s stops wrinkles before they form", fr: "Le Botox preventif dans la vingtaine empeche les rides avant qu'elles ne se forment" },
  { tag: "BOTOX", en: "TrapTox slims the neck-shoulder area & relieves chronic tension headaches", fr: "Le TrapTox affine la zone cou-epaules et soulage les maux de tete" },
  { tag: "BOTOX", en: "The Lip Flip: subtle fullness without filler — results in 6-8 weeks", fr: "Le Lip Flip: volume subtil sans filler — resultats en 6-8 semaines" },
  { tag: "BOTOX", en: "Botox reduces sweating by up to 87% — results last 6-12 months", fr: "Le Botox reduit la transpiration jusqu'a 87% — resultats de 6-12 mois" },
  { tag: "FILLERS", en: "Fillers restore structural support — not just volume. It's about foundation.", fr: "Les fillers restaurent le soutien structural — pas seulement le volume." },
  { tag: "FILLERS", en: "Natural results come from layering small amounts over time", fr: "Les resultats naturels viennent de petites quantites en couches successives" },
  { tag: "SCULPTRA", en: "Sculptra stimulates YOUR collagen — results last 2+ years naturally", fr: "Sculptra stimule VOTRE collagene — resultats naturels de 2+ ans" },
  { tag: "SKIN", en: "Skin boosters improve texture & glow without changing your face shape", fr: "Les boosters ameliorent la texture et l'eclat sans changer la forme du visage" },
  { tag: "PRP", en: "Vampire Facial (PRP + Micro) triggers 200-300% more collagen production", fr: "Le Vampire Facial (PRP + Micro) stimule 200-300% plus de collagene" },
];

const buildSeasonalPromos = (isFr) => {
  const month = new Date().getMonth();
  const winter = [
    isFr ? "Nouveau Vous — Forfait 6 Seringues 2,199$" : "New Year, New You — 6 Syringe Package $2,199",
    isFr ? "Sculptra + Full Upper Face — 1,299$" : "Sculptra + Full Upper Face — $1,299",
    isFr ? "Vampire Facelift (PRP + Micro) — 750$" : "Vampire Facelift (PRP + Micro) — $750",
    isFr ? "Nefertiti Jaw & Neck Lift — 479$" : "Nefertiti Jaw & Neck Lift — $479",
    isFr ? "Micro-aiguilles x3 — 1,099$ (Meilleure valeur)" : "Micro Needling x3 — $1,099 (Best Value)",
  ];
  const spring = [
    isFr ? "Summer Glow — Big 3 499$ +tax" : "Summer Glow Event — Big 3 $499 +tax",
    isFr ? "Full Upper Face — 599$ (7 zones)" : "Full Upper Face — $599 (7 Zones)",
    isFr ? "Lip Filler + Lip Flip GRATUIT — 499$" : "Lip Filler + FREE Lip Flip — $499",
    isFr ? "2 Seringues + Tox GRATUIT — 950$" : "2 Syringes + FREE Tox Package — $950",
    isFr ? "Micro-aiguilles + PRP — 549$" : "Micro Needling + PRP — $549",
  ];
  const summer = [
    isFr ? "Summer Glow — Lip Filler + Lip Flip GRATUIT 499$" : "Summer Glow — Lip Filler + FREE Lip Flip $499",
    isFr ? "Big 3 Package — 499$ +tax" : "THE BIG 3 Package — $499 +tax",
    isFr ? "PRP Therapy — 699$ (Eclat naturel)" : "PRP Therapy — $699 (Natural Glow)",
    isFr ? "TMJ/Masseters — 549$" : "TMJ/Masseter Slimming — $549",
    isFr ? "Forfait 5 Seringues — 1,899$" : "5 Syringe Package — $1,899",
  ];
  const fall = [
    isFr ? "Fall Refresh — Sculptra + Full Upper Face 1,299$" : "Fall Refresh — Sculptra + Full Upper Face $1,299",
    isFr ? "Nefertiti Jaw & Neck Lift — 479$" : "Nefertiti Jaw & Neck Lift — $479",
    isFr ? "Vampire Facelift — 750$" : "Vampire Facelift — $750",
    isFr ? "8 Seringues — 2,899$ (Notre plus gros forfait)" : "8 Syringe Package — $2,899 (Our Biggest)",
    isFr ? "Neck Bands Botox — 279$" : "Neck Bands Botox — $279",
  ];
  if (month === 11 || month === 0 || month === 1) return { promos: winter, Icon: Snowflake, tag: isFr ? "HIVER" : "WINTER" };
  if (month >= 2 && month <= 4) return { promos: spring, Icon: Sun, tag: isFr ? "ÉTÉ" : "SUMMER" };
  if (month >= 5 && month <= 7) return { promos: summer, Icon: Sun, tag: isFr ? "ETE" : "SUMMER" };
  return { promos: fall, Icon: Leaf, tag: isFr ? "AUTOMNE" : "FALL" };
};

const TopBar = () => {
  const { language, t } = useLanguage();
  const isFr = language === "fr";
  const [tickIdx, setTickIdx] = useState(0); // ticks once every interval; modulo'd into the messages array
  const [fade, setFade] = useState(true);
  const [seasonalIdx, setSeasonalIdx] = useState(0);
  const [treatmentIdx, setTreatmentIdx] = useState(() => Math.floor(Math.random() * ALL_TREATMENTS.length));
  const [eduIdx, setEduIdx] = useState(0);
  // Cycles through first names of clients whose birthday is TODAY —
  // refreshed every time `/api/topbar/config` is fetched so signups
  // that happen at 9 AM appear in the afternoon rotation automatically.
  const [birthdayIdx, setBirthdayIdx] = useState(0);
  // Admin-controlled config: which built-in messages are on, plus custom messages
  const [defaultsEnabled, setDefaultsEnabled] = useState(null); // null = not yet loaded → show all
  const [customMessages, setCustomMessages] = useState([]);
  const [birthdayNames, setBirthdayNames] = useState([]);

  const tipIdx = getDailyTip(tips);
  const tip = tips[tipIdx];
  const tipText = isFr ? tip.fr : tip.en;
  const shortTip = tipText.length > 95 ? tipText.slice(0, 92) + "..." : tipText;
  const seasonal = useMemo(() => buildSeasonalPromos(isFr), [isFr]);

  useEffect(() => {
    // Fetch admin config (toggles + custom messages + this-month birthdays).
    // Re-runs whenever the calendar date changes (handled below) so a new
    // month auto-loads a fresh name list without a hard refresh.
    let cancelled = false;
    const load = () => {
      fetch(`${API_URL}/api/topbar/config`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (cancelled || !data) return;
          setDefaultsEnabled(data.defaults_enabled || {});
          setCustomMessages(data.custom_messages || []);
          setBirthdayNames(Array.isArray(data.birthday_names) ? data.birthday_names : []);
        })
        .catch(() => {});
    };
    load();
    // Re-fetch every 30 minutes — covers (a) month rollover at midnight,
    // (b) new client signups mid-month, (c) admin toggling messages on/off.
    const refresh = setInterval(load, 30 * 60 * 1000);
    return () => { cancelled = true; clearInterval(refresh); };
  }, []);

  // Build the unified rotating messages list. Each default message is keyed by
  // a stable id so the admin can toggle it on/off via the Manage Top Bar tab.
  const messages = useMemo(() => {
    const enabled = (key) => defaultsEnabled === null || defaultsEnabled[key] !== false;
    const list = [];

    if (enabled("seasonal")) {
      list.push({
        key: "seasonal",
        eyebrow: seasonal.tag,
        icon: <seasonal.Icon className="w-3.5 h-3.5 text-amber-300" />,
        text: seasonal.promos[seasonalIdx % seasonal.promos.length],
        cta: isFr ? "Voir" : "See Offer",
        href: "/promotions",
      });
    }
    if (enabled("sms")) {
      list.push({
        key: "sms",
        icon: <MessageSquare className="w-3.5 h-3.5" />,
        text: isFr ? "Textez-nous pour prendre rendez-vous" : "Text us to book an appointment",
        suffix: "(613) 850-0805",
        cta: isFr ? "Texter" : "Text",
        href: "sms:613-850-0805?body=Hi, I'd like to book an appointment",
      });
    }
    if (enabled("concierge")) {
      // Premium concierge framing — turns messaging into a feature, not a
      // workaround. Always wins for clients who prefer human contact.
      list.push({
        key: "concierge",
        eyebrow: isFr ? "CONCIERGERIE" : "CONCIERGE",
        icon: <MessageSquare className="w-3.5 h-3.5 text-amber-300" />,
        text: isFr
          ? "Notre équipe est à un message près — heureuse de réserver pour vous"
          : "Our team is one message away — happy to book for you",
        cta: isFr ? "Nous écrire" : "Message Us",
        href: "https://m.me/PreciseRNInjectionist",
      });
    }
    if (enabled("referral")) {
      list.push({
        key: "referral",
        icon: <Gift className="w-3.5 h-3.5 text-emerald-300" />,
        text: isFr ? "Référez un ami — Vous: $50 · Votre ami: $25 chez Karine Lauzon RN!" : "Refer a friend — You: $50 · Your friend: $25 at Karine Lauzon RN!",
        cta: isFr ? "Partager" : "Share Now",
        highlight: true,
        onClick: () => window.dispatchEvent(new CustomEvent("open-referral-popup")),
      });
    }
    if (enabled("tanya")) {
      list.push({
        key: "tanya",
        eyebrow: isFr ? "NOUVEAU" : "NEW",
        icon: <Sparkles className="w-3.5 h-3.5 text-rose-300" />,
        text: isFr
          ? "HydraFacials & traitements laser à Ottawa — Rencontrez notre équipe!"
          : "HydraFacials & Laser Treatments at Ottawa — Meet Our Team!",
        cta: isFr ? "Rencontrer" : "Meet Team",
        href: "/team",
      });
    }
    if (enabled("signup")) {
      list.push({
        key: "signup",
        icon: <Sparkles className="w-3.5 h-3.5 text-amber-300" />,
        text: isFr ? "Inscrivez-vous et obtenez 60$ en crédit instantanément!" : "Sign up & get $60 credit instantly!",
        cta: isFr ? "S'inscrire" : "Sign Up",
        href: "/my-appointments",
      });
    }
    if (enabled("crown")) {
      list.push({
        key: "crown",
        eyebrow: "CROWN RESERVE",
        icon: <Crown className="w-3.5 h-3.5 text-amber-300" />,
        text: isFr ? "Inscrivez-vous et recevez 1000 Crown Points gratuits (60$ crédit)!" : "Sign up & get 1,000 free Crown Points ($60 credit)!",
        cta: isFr ? "S'inscrire" : "Sign Up",
        href: "/my-appointments",
      });
    }
    if (enabled("tip")) {
      list.push({
        key: "tip",
        eyebrow: isFr ? "CONSEIL" : "DAILY TIP",
        icon: <Lightbulb className="w-3.5 h-3.5 text-amber-300" />,
        text: shortTip,
        cta: isFr ? "En savoir +" : "Read More",
        href: "/aesthetic-guide?tab=tip",
      });
    }
    if (enabled("locations")) {
      list.push({
        key: "locations",
        icon: <Star className="w-3.5 h-3.5 text-amber-300" />,
        text: isFr ? "3 emplacements: Ottawa, Kanata, Carleton Place" : "3 locations: Ottawa, Kanata, Carleton Place",
      });
    }
    if (enabled("service")) {
      // Random featured treatment from across the entire site catalog
      const tr = ALL_TREATMENTS[treatmentIdx % ALL_TREATMENTS.length];
      const trName = isFr ? (tr.nameFr || tr.nameEn) : tr.nameEn;
      list.push({
        key: "service",
        eyebrow: isFr ? "TRAITEMENT" : "FEATURED",
        icon: <Sparkles className="w-3.5 h-3.5 text-amber-300" />,
        text: `${trName} — ${tr.price}`,
        cta: isFr ? "Voir" : "View",
        href: tr.href,
      });
    }
    if (enabled("afterpay")) {
      list.push({
        key: "afterpay",
        icon: <span className="bg-[#b2fce4] text-black text-[9px] font-bold px-1.5 py-0.5 rounded tracking-wide">afterpay</span>,
        // Mobile shows the short version (was getting truncated on iPhone widths).
        // Full marketing line appears on sm+ where there's room.
        text: isFr
          ? (<><span className="sm:hidden">Payez en 4 sans intérêt</span><span className="hidden sm:inline">Payez en 4 versements sans intérêt • Aucune vérification de crédit</span></>)
          : (<><span className="sm:hidden">Pay in 4 · Interest-free</span><span className="hidden sm:inline">Pay in 4 interest-free installments • No credit check</span></>),
        cta: isFr ? "En savoir +" : "Learn More",
        href: "/financing",
      });
    }
    // Now Hiring — Medical Aesthetician. Iter 378 · Feb 20 2026 · Karine
    // asked to promote the open role beyond the Careers page so people
    // notice it while browsing anything else on the site. Toggleable by
    // admin via `hiring` slide-config key so we can retire it once the
    // role is filled.
    if (enabled("hiring")) {
      list.push({
        key: "hiring",
        eyebrow: isFr ? "NOUS EMBAUCHONS" : "NOW HIRING",
        icon: <Megaphone className="w-3.5 h-3.5 text-rose-300" />,
        text: isFr
          ? (<><span className="sm:hidden">Esthéticienne médicale — postulez</span><span className="hidden sm:inline">Esthéticienne médicale · 5+ ans · Potenza · HydraFacial · Laser Genesis · postulez maintenant</span></>)
          : (<><span className="sm:hidden">Medical Aesthetician — apply now</span><span className="hidden sm:inline">Medical Aesthetician · 5+ yrs · Potenza · HydraFacial · Laser Genesis · apply now</span></>),
        cta: isFr ? "Postuler" : "Apply",
        href: "/careers",
        highlight: true,
      });
    }
    if (enabled("edu")) {
      const e = EDUCATION_SNIPPETS[eduIdx % EDUCATION_SNIPPETS.length];
      list.push({
        key: "edu",
        eyebrow: e.tag,
        icon: <BookOpen className="w-3.5 h-3.5 text-amber-300" />,
        text: isFr ? e.fr : e.en,
        cta: isFr ? "En savoir +" : "Learn More",
        href: "/aesthetic-guide",
      });
    }
    if (enabled("higher_edu")) {
      list.push({
        key: "higher_edu",
        eyebrow: "NEW",
        icon: <GraduationCap className="w-3.5 h-3.5 text-amber-300" />,
        text: isFr ? "Centre de Haute Education — Botox, Fillers, Sculptra & Plus" : "Higher Education Center — Botox, Fillers, Sculptra & More",
        cta: isFr ? "Explorer" : "Explore",
        href: "/aesthetic-guide",
      });
    }

    // Birthday rotation — randomly picks a real first name from clients
    // whose birthday is TODAY. Refreshed every ~30 min via
    // /api/topbar/config so a client who signs up mid-morning still
    // appears in the afternoon rotation without a hard refresh.
    // Privacy: never includes the last name. The backend strips any
    // whitespace-separated suffix; we re-strip on the client as a
    // defense-in-depth layer (e.g. if a future API ever leaks "Sarah Smith"
    // by accident, we will only ever render "Sarah").
    // When 5 clients share the same birthday, `birthdayIdx % length`
    // cycles through all 5 first names so each one is celebrated in
    // rotation — one per orbit of the TopBar carousel.
    if (enabled("birthday") && birthdayNames.length > 0) {
      const rawName = birthdayNames[birthdayIdx % birthdayNames.length] || "";
      const name = String(rawName).trim().split(/[\s,]+/)[0].slice(0, 40);
      if (name) {
        list.push({
          key: "birthday",
          eyebrow: isFr ? "ANNIVERSAIRE" : "BIRTHDAY",
          icon: <Cake className="w-3.5 h-3.5 text-amber-300" />,
          text: isFr
            ? `Joyeux anniversaire à notre cliente ${name} !`
            : `Happy Birthday to our favorite client ${name}!`,
          cta: isFr ? "Lui souhaiter" : "Send love",
          href: "/",
          highlight: true,
        });
      }
    }

    // Append custom admin-managed messages (already filtered to active=true server-side)
    customMessages.forEach((m) => {
      const Icon = CUSTOM_ICON_MAP[m.icon] || Sparkles;
      list.push({
        key: `custom-${m.id}`,
        eyebrow: isFr ? (m.eyebrow_fr || m.eyebrow) : m.eyebrow,
        icon: <Icon className="w-3.5 h-3.5 text-amber-300" />,
        text: isFr ? (m.text_fr || m.text) : m.text,
        cta: isFr ? (m.cta_fr || m.cta) : m.cta,
        href: m.href || undefined,
        highlight: !!m.highlight,
      });
    });

    return list;
  }, [isFr, seasonal, seasonalIdx, treatmentIdx, eduIdx, shortTip, defaultsEnabled, customMessages, birthdayNames, birthdayIdx]);

  // Tick the rotating message every 5s with a soft fade transition
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setTickIdx((i) => i + 1);
        // Whenever the rotation lands back on a "cycling" message (seasonal,
        // featured treatment, edu) advance their internal sub-index for variety.
        // The featured treatment uses a random index so users see a true cross-section
        // of the entire treatment catalog rather than a predictable sequence.
        setSeasonalIdx((i) => i + 1);
        setTreatmentIdx(() => Math.floor(Math.random() * ALL_TREATMENTS.length));
        setEduIdx((i) => i + 1);
        // Cycle to the next birthday name on every tick so the BIRTHDAY
        // slot rotates through ALL clients celebrating this month.
        setBirthdayIdx((i) => i + 1);
        setFade(true);
      }, 280);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const promo = messages[tickIdx % messages.length];

  // One-time gentle pulse the first time a new visitor's rotation lands on
  // the $60 signup offer — draws the eye once, never repeats (localStorage).
  const [signupPulse, setSignupPulse] = useState(false);
  useEffect(() => {
    if (!promo) return;
    if ((promo.key === "signup" || promo.key === "crown") && !localStorage.getItem("kl_60_pulse_seen")) {
      localStorage.setItem("kl_60_pulse_seen", "1");
      setSignupPulse(true);
      const t = setTimeout(() => setSignupPulse(false), 2600);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.key]);

  const handleClick = () => {
    if (!promo) return;
    if (promo.onClick) {
      promo.onClick();
    } else if (promo.href) {
      // Detect our own Messenger / mailto contact links and route them
      // through the in-page ContactModal instead of navigating away (which
      // would otherwise dump clients onto a Facebook login wall).
      const h = String(promo.href).toLowerCase();
      const isContactLink =
        h.includes("m.me/precisernint") ||
        h.startsWith("mailto:bookings@karinelauzon.com") ||
        h.startsWith("mailto:info@karinelauzon.com") ||
        h.startsWith("mailto:karine@karinelauzon.com");
      if (isContactLink) {
        try {
          window.dispatchEvent(
            new CustomEvent("open-contact-modal", { detail: { source: `topbar-${promo.key}` } })
          );
        } catch {
          /* ignore */
        }
        return;
      }
      // sms: protocol or relative URL — both work with location.href
      window.location.href = promo.href;
    }
  };

  return (
    <div className="relative overflow-hidden bg-[#1F1611] text-white py-1.5 md:py-2 px-3 md:px-4 text-xs md:text-sm border-b border-[#B8862B]/30" data-testid="topbar">
      {/* Birthday celebration overlay — only when the rotation lands on the
          BIRTHDAY message. Three balloons drift from right→left with a
          gentle bob; pointer-events-none so they never block the CTA. */}
      {promo?.key === "birthday" && (
        <div className="absolute inset-0 pointer-events-none z-0 birthday-celebration" aria-hidden="true" data-testid="topbar-birthday-balloons">
          <span className="balloon balloon-1" style={{ background: "#f472b6" }} />
          <span className="balloon balloon-2" style={{ background: "#fbbf24" }} />
          <span className="balloon balloon-3" style={{ background: "#60a5fa" }} />
          <span className="balloon balloon-4" style={{ background: "#a78bfa" }} />
          <span className="balloon balloon-5" style={{ background: "#34d399" }} />
        </div>
      )}
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 relative z-10">
        {/* Left: Text Us + Facebook Messenger (desktop only — rotation already covers SMS on mobile) */}
        <div className="hidden md:flex items-center gap-3 md:gap-4 flex-shrink-0">
          <a href="sms:613-850-0805?body=Hi, I'd like to book an appointment"
            className="flex items-center gap-1.5 hover:text-gray-300 transition-colors whitespace-nowrap"
            data-testid="topbar-text-us">
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t("topbar.textUs")}</span>
            <span>(613) 850-0805</span>
          </a>
          <span className="hidden md:inline text-white/30">|</span>
          <button
            type="button"
            onClick={() => openContactModal({ source: "topbar-messenger" })}
            className="hidden md:flex items-center gap-1.5 hover:text-[#0084FF] transition-colors whitespace-nowrap bg-transparent border-0 p-0 cursor-pointer text-white"
            data-testid="topbar-messenger"
            aria-label="Message us"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.2.16.15.26.37.27.6l.05 1.88c.02.56.6.93 1.11.7l2.1-.93c.18-.08.38-.1.57-.06.9.25 1.85.38 2.75.38 5.64 0 10-4.13 10-9.7S17.64 2 12 2zm5.95 7.57l-2.9 4.6c-.46.73-1.44.92-2.13.4l-2.31-1.73a.6.6 0 00-.72 0l-3.12 2.37c-.42.32-.96-.18-.68-.63l2.9-4.6c.46-.73 1.44-.92 2.13-.4l2.31 1.73a.6.6 0 00.72 0l3.12-2.37c.42-.32.96.18.68.63z"/>
            </svg>
            <span className="text-xs">{isFr ? "Messenger" : "Message Us"}</span>
          </button>
        </div>

        {/* Center: unified rotating promo — visible on every breakpoint */}
        {promo && (
          <button
            onClick={handleClick}
            className={`flex-1 min-w-0 flex items-center justify-center gap-2 transition-all duration-300 cursor-pointer hover:opacity-90 ${
              fade ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
            } ${promo.highlight ? "text-amber-300 font-semibold" : "text-white/95"} ${signupPulse ? "kl-banner-pulse" : ""}`}
            data-testid="topbar-promo-rotation"
          >
            <span className="flex-shrink-0">{promo.icon}</span>
            {promo.eyebrow && (
              <span className="hidden sm:inline text-[10px] font-bold tracking-widest opacity-70">
                {promo.eyebrow}
              </span>
            )}
            <span className="tracking-wide truncate">{promo.text}</span>
            {promo.suffix && <span className="font-semibold whitespace-nowrap hidden sm:inline">{promo.suffix}</span>}
            {promo.highlight && (
              <span className="ml-1 px-1.5 py-0.5 bg-amber-500 text-black text-[9px] font-bold rounded-full uppercase whitespace-nowrap">
                hot
              </span>
            )}
            {promo.cta && (
              <>
                <span className="hidden md:inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap">
                  {promo.cta} <ArrowRight className="w-3 h-3" />
                </span>
                {/* Mobile: compact arrow chip so the banner reads as tappable */}
                <span className="md:hidden inline-flex items-center justify-center w-4.5 h-4.5 p-0.5 rounded-full bg-amber-400/90 text-black flex-shrink-0" aria-hidden="true">
                  <ArrowRight className="w-3 h-3" />
                </span>
              </>
            )}
          </button>
        )}

        {/* Right: Earn $50 + Lobby Screen + Staff sign-in (desktop only) */}
        <div className="hidden md:flex items-center gap-3 md:gap-4 flex-shrink-0">
          <a
            href="/refer-a-friend"
            data-testid="topbar-earn-50-pill"
            title={isFr ? "Référez un ami et gagnez 50$" : "Refer a friend & earn $50"}
            className="group relative flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#B8862B] hover:bg-[#C7982E] text-[#1F1611] transition-all duration-200 shadow-sm hover:shadow-[0_0_0_2px_rgba(184,147,63,0.35)]"
          >
            <Gift className="w-3.5 h-3.5 text-[#1F1611]" />
            <span className="text-xs font-semibold tracking-wide whitespace-nowrap">
              {isFr ? "Gagnez 50$" : "Earn $50"}
            </span>
            <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-[#1F1611] animate-pulse" aria-hidden="true" />
          </a>
          <a
            href="/lobby"
            data-testid="topbar-lobby-link"
            title="Open the clinic TV lobby screen"
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-transparent hover:bg-[#B8862B]/15 border border-[#B8862B]/60 text-[#EEDEC0] hover:text-white transition-colors"
          >
            <Tv className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">{isFr ? "Écran TV" : "Lobby TV"}</span>
          </a>
          <a
            href="/staff/login"
            data-testid="topbar-staff-signin"
            title="Staff sign-in"
            className="flex items-center gap-1.5 text-white/70 hover:text-[#B8862B] transition-colors"
          >
            <Shield className="w-3.5 h-3.5" />
            <span className="text-xs">{isFr ? "Équipe" : "Staff"}</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
